/**
 * WorkerDashboard — Дашборд Работника (роль worker).
 *
 * Симметрия с DashboardInsights (админский Дашборд): приветствие + ИИ-советник
 * (WorkerAdvisor: ИИ-строка, тайлы, карточки-действия) + онбординг для cold.
 * Один фетч GET /api/tasks/worker-advisor; ИИ-строка догружается отдельно (live).
 *
 * Онбординг Работника (OnboardingChecklist) живёт здесь — это его «домашний» экран.
 */
import { useEffect, useState, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ru, enUS } from 'date-fns/locale';
import { useAuth } from '../hooks/useAuth';
import { api } from '../api/client';
import { track } from '../analytics';
import WorkerAdvisor from '../components/WorkerAdvisor';
import OnboardingChecklist from '../components/OnboardingChecklist';
import EquipmentScanFlow from '../components/EquipmentScanFlow';
import EquipmentQueue from '../components/EquipmentQueue';
import Icon from '../components/Icon';
import { usePerm } from '../hooks/usePerm';
import { usePlan } from '../hooks/usePlan';
import FabSpeedDial from '../components/FabSpeedDial';
import ExpenseFab from '../components/ExpenseFab';
import { useHasTags } from '../hooks/useHasTags';
import { useChannel } from '../components/channel/ChannelProvider';
import { RadioIcon } from '../components/channel/ChannelFab';

// Форма Работы тяжёлая — грузим лениво, как на DashboardInsights.
const TaskModal = lazy(() => import('../components/TaskModal'));

function WorkerEmptyState({ user, t, navigate }) {
  const ch = useChannel();
  // «Канал» — только когда контакт куратор (человек из системы) и ему дойдёт @;
  // контакт организации (телефон/почта орги) в Канал не упомянешь.
  const channelCurator = user?.curator && ch && ch.canMention(user.curator.id) ? user.curator : null;
  // Контакт: куратор → телефон организации → email орги → ничего
  const contact = user?.curator
    ? { label: t('workerAdvisor.emptyState.contactLabel'), name: user.curator.name, phone: user.curator.phone, email: user.curator.email }
    : (user?.org_phone || user?.org_email)
      ? { label: t('workerAdvisor.emptyState.contactOrgLabel'), name: user?.org_name, phone: user?.org_phone, email: user?.org_email }
      : null;

  return (
    <div
      style={{
        background: 'var(--bg2)',
        border: '1px solid var(--border, #e0e0e8)',
        borderRadius: 14,
        padding: '20px',
        marginBottom: 16,
      }}
      data-ph-id="worker-empty-state"
    >
      {/* Title + body */}
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
        🎉 {t('workerAdvisor.emptyState.title')}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.5 }}>
        {t('workerAdvisor.emptyState.body')}
      </div>

      {/* Кнопка настройки уведомлений */}
      <button
        onClick={() => { track('worker_empty_state_notif_clicked'); navigate('/profile', { state: { tab: 'notifications' } }); }}
        style={{
          display: 'inline-block',
          padding: '8px 16px',
          background: 'var(--accent)',
          color: '#fff',
          border: 'none', borderRadius: 9,
          fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}
        data-ph-id="worker-empty-notif-btn"
      >
        {t('workerAdvisor.emptyState.notifBtn')}
      </button>

      {/* Разделитель + контакт */}
      {contact && (
        <>
          <div style={{ height: 1, background: 'var(--border, #e0e0e8)', margin: '16px 0' }} />
          {/* flexWrap: 'wrap' — на Android имя организации/кнопки рендерятся шире, чем
              на iOS, и при длинном названии (как «Aimtex House Contracting») строка
              с кнопками «Позвонить»/«Написать» не влезала и уезжала за край экрана —
              justify-content: space-between сам не переносит контент, только
              распределяет лишнее место. minWidth: 0 на левом блоке нужен, чтобы имя
              могло сжаться/перенестись, а не диктовать ширину строки. */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 3 }}>
                {contact.label}
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{contact.name}</div>
              {contact.phone && (
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{contact.phone}</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {contact.phone && (
                <a
                  href={`tel:${contact.phone}`}
                  onClick={() => track('worker_empty_state_contact_call')}
                  style={{
                    padding: '7px 14px',
                    background: 'var(--bg2, #f5f5f7)',
                    border: '1px solid var(--border)',
                    borderRadius: 9, fontSize: 13, fontWeight: 600,
                    color: 'var(--text)', textDecoration: 'none',
                  }}
                  data-ph-id="worker-empty-contact-call"
                >
                  📞 {t('workerAdvisor.emptyState.contactCall')}
                </a>
              )}
              {contact.email && (
                <a
                  href={`mailto:${contact.email}`}
                  onClick={() => track('worker_empty_state_contact_email')}
                  style={{
                    padding: '7px 14px',
                    background: 'var(--bg2, #f5f5f7)',
                    border: '1px solid var(--border)',
                    borderRadius: 9, fontSize: 13, fontWeight: 600,
                    color: 'var(--text)', textDecoration: 'none',
                  }}
                  data-ph-id="worker-empty-contact-email"
                >
                  ✉️ {t('workerAdvisor.emptyState.contactEmail')}
                </a>
              )}
              {channelCurator && (
                <button
                  type="button"
                  onClick={() => {
                    track('worker_empty_state_contact_channel');
                    ch.mentionInChannel({ userId: channelCurator.id, name: channelCurator.name, scope: null });
                  }}
                  style={{
                    padding: '7px 14px',
                    background: 'var(--bg2, #f5f5f7)',
                    border: '1px solid var(--border)',
                    borderRadius: 9, fontSize: 13, fontWeight: 600,
                    color: 'var(--text)', cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                  data-ph-id="worker-empty-contact-channel"
                  data-ph-capture
                >
                  <span style={{ color: 'var(--accent)', display: 'flex' }}><RadioIcon size={15} /></span>
                  {t('workerAdvisor.emptyState.contactChannel')}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function WorkerDashboard() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePerm();
  const dateLocale = i18n.language === 'en' ? enUS : ru;

  const [data, setData] = useState(null);
  const [lineLoading, setLineLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  // Нет выпущенных наклеек — кнопка сканера не нужна.
  const hasTags = useHasTags({ enabled: can('equipment.view') });
  const { hasFeature } = usePlan();

  // FAB «+» и «Рация». Узкая область (Работник, Бригадир) попадает сюда, а не на
  // DashboardInsights, где FAB есть, — поэтому кнопка нужна и здесь.
  // «Сканировать наклейку» FabSpeedDial добавляет сам.
  const [showNewTask, setShowNewTask] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [showExpenseFab, setShowExpenseFab] = useState(false);
  const canNewTask = can('tasks.create');
  const canExpenses = !user?.is_demo && hasFeature('expenses') && can('expenses.create');

  // Чеклист сам сообщает, что он скрыт (метка per-user на сервере, не в
  // localStorage) — от этого зависит показ empty-state ниже.
  const [checklistHidden, setChecklistHidden] = useState(false);

  useEffect(() => {
    let alive = true;
    const lang = i18n.language === 'en' ? 'en' : 'ru';
    setLineLoading(false);
    // Фаза 1: мгновенный детерминированный контент (тайлы/карточки).
    api.get(`/tasks/worker-advisor?lang=${lang}`)
      .then(r => {
        if (!alive) return;
        setData(r);
        // Фаза 2: ИИ-строка догружается отдельно (live, cache-miss ~1с).
        if (r && r.state === 'live' && !r.line) {
          setLineLoading(true);
          api.get(`/tasks/worker-advisor/line?lang=${lang}`)
            .then(res => { if (alive) setData(d => ({ ...d, line: res.line })); })
            .catch(() => {})
            .finally(() => { if (alive) setLineLoading(false); });
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [i18n.language, reloadKey]);

  const firstName = (user?.name || '').split(' ')[0];

  return (
    <div style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 96px)' }}>
      <div className="page-header page-header--scroll">
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>
            {firstName ? t('dashboardNew.greetingName', { name: firstName }) : t('dashboardNew.greeting')}
          </h2>
          <div className="subtitle">{format(new Date(), 'EEEE, d MMMM', { locale: dateLocale })}</div>
        </div>
      </div>

      <div style={{ padding: '12px 0 0' }}>
        {/* Сканер — на самом верху и без обёрток.
            ⚠️ Нативная камера телефона сюда не приведёт: Fixty стоит как
            standalone-PWA, ссылка из QR открывается в Safari, а это отдельное
            хранилище и отдельная сессия. Для сотрудника единственный вход,
            который не выкидывает на логин, — камера внутри приложения, и
            прятать её в подраздел значит закопать основной сценарий фичи. */}
        {can('equipment.view') && hasTags && (
          <div style={{ padding: '0 16px 12px' }}>
            <button
              className="btn"
              data-ph-capture="equipment_scan_open"
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              onClick={() => { setScanning(true); track('equipment_scanner_opened', { source: 'worker_dashboard' }); }}
            >
              <Icon name="scan" size={16} /> {t('equipment.scanTag')}
            </button>
          </div>
        )}

        {/* Очередь подтверждения. Компонент сам решает, показываться ли:
            нет права подтверждать или очередь пуста — не рендерится вовсе. */}
        <div style={{ padding: '0 16px' }}>
          <EquipmentQueue />
        </div>

        {/* warming/live рисует сам; cold — молчит (ниже онбординг). */}
        <WorkerAdvisor data={data} lineLoading={lineLoading} />

        {/* Онбординг Работника — сам прячется, когда все шаги пройдены. */}
        <div style={{ padding: '0 16px' }}>
          <OnboardingChecklist onDismissed={() => setChecklistHidden(true)} />

          {/* Empty-state: нет работ (cold) + чеклист скрыт */}
          {checklistHidden && data?.state === 'cold' && (
            <WorkerEmptyState user={user} t={t} navigate={navigate} />
          )}
        </div>
      </div>

      <FabSpeedDial
        actions={[
          ...(canNewTask ? [{
            label: t('tasks.newTask'),
            color: '#2563EB',
            onClick: () => { setShowNewTask(true); track('fab_clicked', { screen: 'dashboard' }); },
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
          }] : []),
          ...(canExpenses ? [{
            label: t('expenses.newExpense'),
            color: '#0d7a4e',
            onClick: () => { setShowExpenseFab(true); track('expense_fab_clicked', { screen: 'dashboard' }); },
            icon: <span style={{ fontSize: 18, fontWeight: 700, lineHeight: 1 }}>$</span>,
          }] : []),
        ]}
      />

      {showNewTask && (
        <Suspense fallback={null}>
          <TaskModal
            onClose={() => setShowNewTask(false)}
            onSave={() => { setShowNewTask(false); setReloadKey(k => k + 1); }}
          />
        </Suspense>
      )}

      {canExpenses && (
        <ExpenseFab
          openSheet={showExpenseFab}
          onSheetClose={() => setShowExpenseFab(false)}
          allowPropertyPick
        />
      )}

      {scanning && (
        <EquipmentScanFlow
          onClose={() => setScanning(false)}
          // Своего маршрута у Работы нет — она раскрывается внутри страницы
          // Объекта. Тот же приём, что у карточек WorkerAdvisor.
          onOpenTask={(taskId, propId) => propId && navigate(`/properties/${propId}`, {
            state: { openTaskId: taskId, from: '/dashboard' },
          })}
        />
      )}
    </div>
  );
}
